// GuestEmailsManager — sent log + template preview + manual trigger.
import React, { useCallback, useEffect, useState } from 'react';
import { Mail, RefreshCw, Loader2, Eye, Zap, Play, Send, Inbox } from 'lucide-react';
import { apiCall } from './_shared';
import { MailStatusCard } from './MailStatusCard';
import WinbackCard from './WinbackCard';
import Beds24ConnectCard from './Beds24ConnectCard';
import WhatsAppStatusCard from './WhatsAppStatusCard';
import PromisesCard from './PromisesCard';

const KIND_LABEL = { pre_arrival: 'Pre-arrival', post_stay: 'Post-stay', booking_welcome: 'Welcome', arrival_pack: 'Arrival pack' };
const KIND_STYLE = { pre_arrival: 'bg-blue-100 text-blue-700', post_stay: 'bg-green-100 text-green-700', booking_welcome: 'bg-orange-100 text-orange-700', arrival_pack: 'bg-violet-100 text-violet-700' };

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const GuestEmailsManager = ({ userRole }) => {
  const [data, setData] = useState({ items: [], counts: { pre_arrival: 0, post_stay: 0 } });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMeta, setPreviewMeta] = useState({ kind: 'pre_arrival', lang: 'en' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [log, ana] = await Promise.all([
        apiCall('/api/admin/emails/log?limit=200'),
        apiCall('/api/admin/emails/click-analytics?days=90'),
      ]);
      setData(log);
      setAnalytics(ana);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const preview = async (kind, lang) => {
    setBusy(true);
    try {
      const json = await apiCall(`/api/admin/emails/preview?kind=${kind}&lang=${lang}`);
      setPreviewHtml(json.html);
      setPreviewMeta({ kind, lang, subject: json.subject });
    } catch (e) {
      alert('Preview failed: ' + e.message);
    } finally { setBusy(false); }
  };

  const runDaily = async () => {
    if (!window.confirm('Run today\'s daily send now? (Pre-arrival for tomorrow\'s arrivals, post-stay for yesterday\'s check-outs)')) return;
    setBusy(true);
    try {
      const json = await apiCall('/api/admin/emails/run-daily', { method: 'POST' });
      alert(`Done ✓\nPre-arrival sent: ${json.pre_sent} · skipped: ${json.pre_skipped}\nPost-stay sent: ${json.post_sent} · skipped: ${json.post_skipped}`);
      await load();
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally { setBusy(false); }
  };

  // Send a real email to any address using a synthetic booking. Great for
  // dogfooding the template against your own inbox before real arrivals.
  const [testForm, setTestForm] = useState({ to_email: '', kind: 'pre_arrival', lang: 'en', room_id: '' });
  const testSend = async (e) => {
    e?.preventDefault();
    if (!testForm.to_email.includes('@')) { alert('Please enter a valid email address.'); return; }
    setBusy(true);
    try {
      const payload = {
        to_email: testForm.to_email.trim(),
        kind: testForm.kind,
        lang: testForm.lang,
      };
      if (testForm.room_id) payload.room_id = Number(testForm.room_id);
      const json = await apiCall('/api/admin/emails/test-send', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      alert(`Sent ✓ to ${json.recipient} (email id: ${json.email_id || '—'})`);
      await load();
    } catch (e) {
      alert('Test send failed: ' + e.message);
    } finally { setBusy(false); }
  };

  // Fire either pre_arrival or post_stay for a specific booking id.
  const [manualForm, setManualForm] = useState({ booking_id: '', kind: 'pre_arrival' });
  const manualSend = async (e) => {
    e?.preventDefault();
    const bid = manualForm.booking_id.trim();
    if (!bid) { alert('Booking id is required.'); return; }
    setBusy(true);
    try {
      const json = await apiCall(`/api/admin/emails/send/${encodeURIComponent(bid)}?kind=${manualForm.kind}`, { method: 'POST' });
      alert(`Sent ✓ to ${json.recipient} (${json.lang})`);
      await load();
    } catch (e) {
      alert('Send failed: ' + e.message);
    } finally { setBusy(false); }
  };

  // Rows hoisted OUTSIDE JSX (Babel workaround).
  const rows = (data.items || []).map((i) => (
    <tr key={(i.email_id || '') + i.sent_at + i.recipient} className="border-b last:border-b-0 hover:bg-gray-50">
      <td className="py-2 pr-3 text-sm text-gray-600">{fmt(i.sent_at)}</td>
      <td className="py-2 pr-3">
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${KIND_STYLE[i.kind] || 'bg-gray-100 text-gray-700'}`}>
          {KIND_LABEL[i.kind] || i.kind}
        </span>
      </td>
      <td className="py-2 pr-3 text-sm text-gray-800">{i.recipient}</td>
      <td className="py-2 pr-3 text-xs text-gray-500 uppercase">{i.lang}</td>
      <td className="py-2 pr-3 text-xs font-mono text-gray-500">{i.booking_id || '—'}</td>
      <td className="py-2 pr-3 text-xs font-mono text-orange-600">{i.coupon_code || '—'}</td>
    </tr>
  ));

  const previewButtons = [];
  for (const k of ['pre_arrival', 'post_stay']) {
    for (const l of ['en', 'me', 'sq']) {
      previewButtons.push(
        <button key={k + l} onClick={() => preview(k, l)}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded uppercase tracking-wide"
          data-testid={`emails-preview-${k}-${l}`}>
          {k === 'pre_arrival' ? 'Pre' : 'Post'} · {l}
        </button>
      );
    }
  }

  const analyticsRows = (analytics?.breakdown || []).map((r, i) => (
    <tr key={i} className="border-b last:border-b-0">
      <td className="py-2 pl-3 pr-3 text-sm font-medium text-gray-800">{r.kind}</td>
      <td className="py-2 pr-3 text-xs uppercase text-gray-500">{r.lang}</td>
      <td className="py-2 pr-3 text-sm text-gray-600 text-right">{r.sends}</td>
      <td className="py-2 pr-3 text-sm font-semibold text-orange-600 text-right">{r.clicks}</td>
      <td className="py-2 pr-3 text-sm font-semibold text-gray-900 text-right">
        {r.ctr_pct === null ? '—' : `${r.ctr_pct}%`}
      </td>
    </tr>
  ));

  return (
    <div className="space-y-6" data-testid="guest-emails-manager">
      <MailStatusCard />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-7 h-7 text-orange-500" /> Guest Emails
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Automatic pre-arrival (T-1, 24h before check-in) & post-stay (T+1) sends. Manual triggers below.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} disabled={loading || busy}
            className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button onClick={runDaily} disabled={busy}
            className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded text-sm hover:bg-orange-600 font-semibold disabled:opacity-50"
            data-testid="emails-run-daily">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run daily now
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>
      )}

      <PromisesCard canEdit={['super_admin', 'admin', 'manager'].includes(userRole)} onPreview={preview} />

      {/* Counts */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border p-4" data-testid="emails-kpi-pre">
          <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Pre-arrival sent</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{data.counts.pre_arrival}</p>
        </div>
        <div className="bg-white rounded-xl border p-4" data-testid="emails-kpi-post">
          <p className="text-xs uppercase tracking-wider text-green-600 font-semibold">Post-stay sent</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{data.counts.post_stay}</p>
        </div>
      </div>

      {/* Send panels — test-send & booking-id send */}
      <div className="grid md:grid-cols-2 gap-4">
        <form onSubmit={testSend} className="bg-white rounded-xl border p-4 space-y-3" data-testid="emails-test-send-form">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-500" />
            <h3 className="font-semibold text-gray-900">Test send to any inbox</h3>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Fires a real email using a synthetic booking. Use this to preview the template in Gmail / your phone before arrivals.
          </p>
          <input
            type="email"
            required
            placeholder="you@larosa-negra.com"
            value={testForm.to_email}
            onChange={(e) => setTestForm({ ...testForm, to_email: e.target.value })}
            className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-orange-500"
            data-testid="emails-test-email-input"
          />
          <div className="grid grid-cols-3 gap-2">
            <select
              value={testForm.kind}
              onChange={(e) => setTestForm({ ...testForm, kind: e.target.value })}
              className="px-2 py-2 border rounded text-sm"
              data-testid="emails-test-kind-select"
            >
              <option value="pre_arrival">Pre-arrival</option>
              <option value="post_stay">Post-stay</option>
              <option value="booking_welcome">Welcome (step 2)</option>
              <option value="arrival_pack">Arrival pack (step 3)</option>
            </select>
            <select
              value={testForm.lang}
              onChange={(e) => setTestForm({ ...testForm, lang: e.target.value })}
              className="px-2 py-2 border rounded text-sm"
              data-testid="emails-test-lang-select"
            >
              <option value="en">EN</option>
              <option value="me">ME/RS</option>
              <option value="sq">SQ</option>
              <option value="de">DE</option>
              <option value="ru">RU</option>
            </select>
            <input
              type="number"
              min="1"
              placeholder="Room id"
              value={testForm.room_id}
              onChange={(e) => setTestForm({ ...testForm, room_id: e.target.value })}
              className="px-2 py-2 border rounded text-sm"
              data-testid="emails-test-roomid-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded font-semibold text-sm disabled:opacity-50"
            data-testid="emails-test-send-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test now
          </button>
        </form>

        <form onSubmit={manualSend} className="bg-white rounded-xl border p-4 space-y-3" data-testid="emails-booking-send-form">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-gray-900">Send for a real booking</h3>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Looks up the booking in Beds24 (last 60d → +1yr) and fires the selected template to the guest's real address.
          </p>
          <input
            type="text"
            required
            placeholder="Beds24 booking id (e.g. 83771235)"
            value={manualForm.booking_id}
            onChange={(e) => setManualForm({ ...manualForm, booking_id: e.target.value })}
            className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 font-mono"
            data-testid="emails-manual-booking-input"
          />
          <select
            value={manualForm.kind}
            onChange={(e) => setManualForm({ ...manualForm, kind: e.target.value })}
            className="w-full px-3 py-2 border rounded text-sm"
            data-testid="emails-manual-kind-select"
          >
            <option value="pre_arrival">Pre-arrival (24h)</option>
            <option value="post_stay">Post-stay (T+1)</option>
            <option value="booking_welcome">Welcome (step 2)</option>
            <option value="arrival_pack">Arrival pack (step 3)</option>
          </select>
          <button
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-semibold text-sm disabled:opacity-50"
            data-testid="emails-manual-send-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Fire template
          </button>
        </form>
      </div>

      {/* A/B click-through analytics */}
      {analytics && (
        <div className="bg-white rounded-xl border p-5" data-testid="emails-ab-analytics">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" /> Click-through by language
            </h3>
            <div className="text-xs text-gray-500">
              <span className="font-semibold text-orange-600">{analytics.total_clicks}</span> clicks / <span className="font-semibold">{analytics.total_sends}</span> sends
              · <span className="font-semibold">{analytics.overall_ctr_pct}%</span> CTR (last 90d)
            </div>
          </div>
          {analytics.breakdown.length === 0 ? (
            <p className="text-sm text-gray-400">No clicks yet — CTAs start tracking automatically once e-mails go out.</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left py-2 pl-3 pr-3">Kind</th>
                  <th className="text-left py-2 pr-3">Lang</th>
                  <th className="text-right py-2 pr-3">Sends</th>
                  <th className="text-right py-2 pr-3">Clicks</th>
                  <th className="text-right py-2 pr-3">CTR</th>
                </tr>
              </thead>
              <tbody>
                {analyticsRows}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Template preview */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-orange-500" /> Template preview
          </h3>
          <div className="flex gap-2 flex-wrap">
            {previewButtons}
          </div>
        </div>
        {previewHtml ? (
          <>
            <p className="text-xs text-gray-500 mb-2">
              <strong>{previewMeta.kind === 'pre_arrival' ? 'Pre-arrival' : 'Post-stay'}</strong> ({previewMeta.lang}) — Subject: <em>{previewMeta.subject}</em>
            </p>
            <div className="border rounded-lg overflow-hidden bg-gray-50">
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                sandbox="allow-same-origin"
                className="w-full"
                style={{ height: 640, border: 0 }}
                data-testid="emails-preview-iframe"
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400">Pick a template above to preview.</p>
        )}
      </div>

      {/* Log */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" /> Send history
          </h3>
          <span className="text-xs text-gray-400">{data.items.length} rows</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left py-2 pl-4 pr-3">Sent at</th>
                <th className="text-left py-2 pr-3">Kind</th>
                <th className="text-left py-2 pr-3">Recipient</th>
                <th className="text-left py-2 pr-3">Lang</th>
                <th className="text-left py-2 pr-3">Booking</th>
                <th className="text-left py-2 pr-3">Coupon</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="py-8 text-center text-sm text-gray-400">No emails sent yet. They go out automatically once e-mail delivery is configured (see the status card above).</td></tr>
              ) : rows}
            </tbody>
          </table>
        </div>
      </div>
      {['super_admin', 'admin', 'manager'].includes(userRole) && <WhatsAppStatusCard />}
      {['super_admin', 'admin'].includes(userRole) && <Beds24ConnectCard />}
      {['super_admin', 'admin', 'manager', 'marketing'].includes(userRole) && <WinbackCard />}
    </div>
  );
};

export default GuestEmailsManager;
